/**
 * Buurt: de eerste vier cijfers van de postcode.
 *
 * De brief vraagt een filter op buurt. De bron levert geen buurtveld, en er is
 * in versie 1 geen tweede bron: geen CBS-polygonen, geen extra tabel. Wat er
 * wél in elke rij zit is de postcode, en het PC4-deel daarvan is in Amsterdam
 * een bruikbare indeling - `1073` is De Pijp, `1015` de Jordaan.
 *
 * `plaats` is geen filter meer zolang er één stad in de data zit: een keuzelijst
 * met precies één waarde versmalt niets.
 *
 * De namen hieronder zijn een leeshulp, geen bestuurlijke waarheid: een PC4-gebied
 * en een officiele buurt lopen niet gelijk. Een onbekende PC4 houdt gewoon zijn
 * nummer - liever een kaal getal dan een verzonnen naam.
 */

/** Een PC4-code: vier cijfers. */
export type Pc4 = string;

/** PC4 uit een postcode als `1091 MS`, `1091ms` of `1091`. Null als het er niet in zit. */
export function pc4Van(postcode: string | null | undefined): Pc4 | null {
  if (!postcode) return null;
  const cijfers = postcode.trim().slice(0, 4);
  return /^\d{4}$/.test(cijfers) ? cijfers : null;
}

/**
 * Amsterdamse PC4-gebieden met de naam waaronder mensen ze kennen.
 * Alleen de codes die in de stad voorkomen; de rest valt terug op het nummer.
 */
export const BUURTNAMEN: Record<Pc4, string> = {
  '1011': 'Nieuwmarkt / Plantage',
  '1012': 'Burgwallen',
  '1013': 'Houthavens / Spaarndammer',
  '1014': 'Westelijk Havengebied',
  '1015': 'Jordaan',
  '1016': 'Grachtengordel-West',
  '1017': 'Grachtengordel-Zuid',
  '1018': 'Weesperbuurt / Oostelijke Eilanden',
  '1019': 'Oostelijk Havengebied',
  '1021': 'Vogelbuurt',
  '1022': 'Nieuwendam',
  '1023': 'Tuindorp Oostzaan',
  '1024': 'Banne Buiksloot',
  '1025': 'Kadoelen',
  '1026': 'Holysloot / Durgerdam',
  '1027': 'Zunderdorp',
  '1028': 'Ransdorp',
  '1031': 'Buiksloot',
  '1032': 'Waterlandpleinbuurt',
  '1033': 'Molenwijk',
  '1034': 'Elzenhagen',
  '1035': 'Nieuwendammerham',
  '1036': 'Cornelis Douwesterrein',
  '1051': 'Frederik Hendrikbuurt',
  '1052': 'Staatsliedenbuurt',
  '1053': 'Kinkerbuurt',
  '1054': 'Oud-West',
  '1055': 'Bos en Lommer',
  '1056': 'Overtoomse Sluis',
  '1057': 'Vondelbuurt',
  '1058': 'Hoofddorppleinbuurt',
  '1059': 'Schinkelbuurt',
  '1060': 'Slotervaart-West',
  '1061': 'Slotermeer',
  '1062': 'Overtoomse Veld',
  '1063': 'Geuzenveld',
  '1064': 'Slotermeer-Noord',
  '1065': 'Slotervaart',
  '1066': 'Nieuw Sloten',
  '1067': 'Osdorp-Noord',
  '1068': 'Osdorp-Midden',
  '1069': 'Osdorp-Zuid',
  '1071': 'Museumkwartier',
  '1072': 'Nieuwe Pijp',
  '1073': 'De Pijp',
  '1074': 'Diamantbuurt',
  '1075': 'Willemspark / Apollobuurt',
  '1076': 'Stadionbuurt',
  '1077': 'Zuidas / Prinses Irenebuurt',
  '1078': 'Rivierenbuurt-Noord',
  '1079': 'Rivierenbuurt',
  '1081': 'Buitenveldert-West',
  '1082': 'Buitenveldert-Oost',
  '1083': 'Buitenveldert-Zuid',
  '1086': 'IJburg-Oost',
  '1087': 'IJburg',
  '1091': 'Weesperzijde / Oosterparkbuurt',
  '1092': 'Dapperbuurt',
  '1093': 'Indische Buurt',
  '1094': 'Watergraafsmeer-West',
  '1095': 'Zeeburgereiland',
  '1096': 'Amstelkwartier',
  '1097': 'Watergraafsmeer',
  '1098': 'Betondorp / Middenmeer',
  '1099': 'Omval / Overamstel',
  '1101': 'Bijlmer-Centrum',
  '1102': 'Bijlmer-Oost',
  '1103': 'Holendrecht',
  '1104': 'Gaasperdam',
  '1105': 'Academisch Medisch Centrum',
  '1106': 'Gaasperplas',
  '1107': 'Driemond',
  '1108': 'Nellestein',
  '1109': 'Amsterdamse Poort',
};

/** De naam bij een PC4, of null als we die niet kennen. */
export function buurtNaam(pc4: Pc4 | null | undefined): string | null {
  if (!pc4) return null;
  return BUURTNAMEN[pc4] ?? null;
}

/** Wat er in de filterbalk staat: `1073 · De Pijp`, of alleen `1073`. */
export function buurtLabel(pc4: Pc4 | null | undefined): string {
  if (!pc4) return 'Onbekend';
  const naam = buurtNaam(pc4);
  return naam ? `${pc4} · ${naam}` : pc4;
}
